import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import {
  Pencil, Mail, Phone, Building2, CreditCard,
  MapPin, Globe, User, ArrowLeft, Camera, X, Check, Ban,
} from "lucide-react";

import { useAuth }           from "../auth/AuthProvider";
import { useFetchProfile }   from "../../hooks/auth/fetchProfile";
import { useUpdateProfile }  from "../../hooks/auth/updateProfile";
import FallbackImage         from "../../components/common/fallbackImage";
import { resolveImageUrl }   from "../../utils/resolveImageUrl";

/* ─────────────────────────────────────────
   VALIDATION
───────────────────────────────────────── */
const profileSchema = Yup.object({
  name:        Yup.string().trim().required("Name is required"),
  email:       Yup.string().trim().email("Invalid email").required("Email is required"),
  phone:       Yup.string().trim().nullable(),
  Country:    Yup.string().trim().nullable(),
  website:     Yup.string().trim().nullable(),
  companyName: Yup.string().trim().nullable(),
});

/* ─────────────────────────────────────────
   READ-ONLY INFO ROW
───────────────────────────────────────── */
const InfoRow = ({ icon: Icon, label, value, href }) => (
  <div style={{
    display: "flex", alignItems: "flex-start", gap: 12,
    padding: "12px 0", borderBottom: "1px solid #f3f4f6",
  }}>
    <span style={{
      width: 34, height: 34, borderRadius: "50%",
      background: "#ede9fe", color: "#5c308d",
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}>
      <Icon size={16} />
    </span>
    <div>
      <p style={{ margin: 0, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#9ca3af" }}>
        {label}
      </p>
      {href && value ? (
        <a href={href} target="_blank" rel="noopener noreferrer"
          style={{ margin: "3px 0 0", fontSize: 14, color: "#5c308d", display: "block", wordBreak: "break-all" }}>
          {value}
        </a>
      ) : (
        <p style={{ margin: "3px 0 0", fontSize: 14, color: value ? "#111827" : "#9ca3af" }}>
          {value || "—"}
        </p>
      )}
    </div>
  </div>
);

/* ─────────────────────────────────────────
   EDIT FIELD
───────────────────────────────────────── */
const EditField = ({ label, name, type = "text", placeholder, formik }) => (
  <div className="form-group" style={{ marginBottom: 0 }}>
    <label style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#6b7280" }}>
      {label}
    </label>
    <input
      name={name}
      type={type}
      placeholder={placeholder}
      value={formik.values[name] ?? ""}
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
      style={{ marginTop: 4 }}
    />
    {formik.touched[name] && formik.errors[name] && (
      <p className="error-text" style={{ marginTop: 2 }}>{formik.errors[name]}</p>
    )}
  </div>
);

/* ─────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────── */
const Profile = () => {
  const navigate                                   = useNavigate();
  const { user: authUser, updateUser }             = useAuth();
  const { data: rawProfile, isLoading, isError }   = useFetchProfile();
  const { mutateAsync: updateProfile, isPending }  = useUpdateProfile();

  const [isEditing,   setIsEditing]   = useState(false);
  const [logoPreview, setLogoPreview] = useState("");
  const [cardPreview, setCardPreview] = useState("");
  const logoInputRef                  = useRef(null);
  const cardInputRef                  = useRef(null);

  /* ── merge raw API data on top of auth context ── */
  const profile = {
    name:        rawProfile?.name        || rawProfile?.companyName  || authUser?.name        || "",
    email:       rawProfile?.email       || rawProfile?.companyEmail || authUser?.email       || "",
    phone:       rawProfile?.phone       || rawProfile?.phoneNumber  || authUser?.phone       || "",
    Country:    rawProfile?.country    || rawProfile?.address      || rawProfile?.city      || authUser?.country || "",
    website:     rawProfile?.website     || rawProfile?.websiteUrl   || rawProfile?.url       || authUser?.website  || "",
    companyName: rawProfile?.companyName || rawProfile?.name         || authUser?.companyName || "",
    designation: rawProfile?.designation || rawProfile?.role         || authUser?.designation || "",
    role:        rawProfile?.role        || rawProfile?.designation  || authUser?.role        || "Admin",
    logo:        rawProfile?.logo        || rawProfile?.logoPath     || rawProfile?.profilePicture || authUser?.logo || "",
    visitingCard:rawProfile?.visitingCard|| rawProfile?.visitingCardPath || rawProfile?.businessCard || authUser?.visitingCard || "",
  };

  const profilePicSrc   = resolveImageUrl(profile.logo)         || "";
  const visitingCardSrc = resolveImageUrl(profile.visitingCard) || "";
  const initial = String(profile.name || profile.email || "A").trim().charAt(0).toUpperCase();

  /* ── FORMIK ─────────────────────────── */
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name:        profile.name        || "",
      email:       profile.email       || "",
      phone:       profile.phone       || "",
      Country:    profile.Country    || "",
      website:     profile.website     || "",
      companyName: profile.companyName || "",
      logo:        null,
      visitingCard: null,
    },
    validationSchema: profileSchema,
    onSubmit: async (values) => {
      const fd = new FormData();
      fd.append("name",  values.name.trim());
      fd.append("email", values.email.trim());
      if (values.phone?.trim())       fd.append("phone",       values.phone.trim());
      if (values.Country?.trim())    fd.append("Country",    values.Country.trim());
      if (values.website?.trim())     fd.append("website",     values.website.trim());
      if (values.companyName?.trim()) fd.append("companyName", values.companyName.trim());
      if (values.logo         instanceof File) fd.append("logo",         values.logo);
      if (values.visitingCard instanceof File) fd.append("visitingCard", values.visitingCard);

      try {
        const res = await updateProfile(fd);
        const fresh = res?.data?.user || res?.data?.company || res?.data || res?.user || res;
        if (fresh) updateUser(fresh);
        toast.success("Profile updated successfully");
        setIsEditing(false);
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to update profile");
      }
    },
  });

  /* reset previews when entering edit mode */
  useEffect(() => {
    if (isEditing) {
      setLogoPreview(profilePicSrc);
      setCardPreview(visitingCardSrc);
    }
  }, [isEditing]);

  const handleFileChange = (field, setPreview) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    formik.setFieldValue(field, file);
    if (file.type.startsWith("image/")) setPreview(URL.createObjectURL(file));
  };

  const cancelEdit = () => {
    formik.resetForm();
    setLogoPreview("");
    setCardPreview("");
    setIsEditing(false);
  };

  /* ── LOADING / ERROR ────────────────── */
  if (isLoading) return <div style={{ padding: 32 }}>Loading profile...</div>;
  if (isError)   return <div style={{ padding: 32 }}>Failed to load profile</div>;

  /* ── RENDER ─────────────────────────── */
  return (
    <div style={{ margin: "0 auto", padding: "24px 16px" }}>

      {/* BACK */}
      <button onClick={() => navigate(-1)} style={{
        display: "flex", alignItems: "center", gap: 6,
        background: "none", border: "none", cursor: "pointer",
        color: "#5c308d", fontWeight: 600, marginBottom: 24, fontSize: 14, padding: 0,
      }}>
        <ArrowLeft size={16} /> Back
      </button>

      {/* ══════════════════════════════════
          PROFILE CARD
      ══════════════════════════════════ */}
      <div style={{
        background: "#fff", borderRadius: 16,
        border: "1px solid #e5e7eb",
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)", overflow: "hidden",
      }}>
        {/* BANNER */}
        <div style={{ height: 110, background: "linear-gradient(135deg, #5c308d 0%, #7c3aed 100%)" }} />

        {/* AVATAR + ACTIONS ROW */}
        <div style={{
          padding: "0 28px", display: "flex",
          justifyContent: "space-between", alignItems: "flex-end", marginTop: -50,
        }}>
          {/* avatar */}
          <div style={{ position: "relative" }}>
            {(isEditing ? logoPreview : profilePicSrc) ? (
              <FallbackImage
                src={isEditing ? logoPreview : profilePicSrc}
                alt={profile.name}
                style={{
                  width: 96, height: 96, borderRadius: "50%", objectFit: "cover",
                  border: "4px solid #fff", boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                }}
              />
            ) : (
              <span style={{
                width: 96, height: 96, borderRadius: "50%",
                background: "#ede9fe", color: "#5c308d",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontWeight: 800, fontSize: 34,
                border: "4px solid #fff", boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              }}>
                {initial}
              </span>
            )}
            {isEditing && (
              <button type="button" onClick={() => logoInputRef.current?.click()} style={{
                position: "absolute", bottom: 2, right: 2,
                width: 28, height: 28, borderRadius: "50%",
                background: "#5c308d", color: "#fff", border: "2px solid #fff",
                display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
              }}>
                <Camera size={13} />
              </button>
            )}
            <input ref={logoInputRef} type="file" accept=".png,.jpg,.jpeg"
              style={{ display: "none" }} onChange={handleFileChange("logo", setLogoPreview)} />
          </div>

          {/* action buttons */}
          <div style={{ display: "flex", gap: 8, }}>
            {isEditing ? (
              <>
                <button type="button" onClick={cancelEdit} style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "8px 16px", borderRadius: 8,
                  border: "1.5px solid #d1d5db", background: "#fff",
                  color: "#6b7280", fontSize: 13, fontWeight: 600, cursor: "pointer",
                }}>
                  <Ban size={14} /> Cancel
                </button>
                <button type="button" onClick={formik.handleSubmit} disabled={isPending} style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "8px 16px", borderRadius: 8,
                  border: "none", background: "#5c308d", color: "#fff",
                  fontSize: 13, fontWeight: 600,
                  cursor: isPending ? "not-allowed" : "pointer",
                  opacity: isPending ? 0.7 : 1,
                }}>
                  <Check size={14} /> {isPending ? "Saving..." : "Save Changes"}
                </button>
              </>
            ) : (
              <button type="button" onClick={() => setIsEditing(true)} style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "8px 16px", borderRadius: 8,
                border: "1.5px solid #5c308d", background: "#fff",
                color: "#5c308d", fontSize: 13, fontWeight: 600, cursor: "pointer",
              }}>
                <Pencil size={14} /> Edit Profile
              </button>
            )}
          </div>
        </div>

        {/* NAME + ROLE */}
        <div style={{ padding: "10px 28px 0" }}>
          {isEditing ? (
            <input
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              style={{
                fontSize: 20, fontWeight: 700, color: "#111827",
                border: "1px solid #d1d5db", borderRadius: 8,
                padding: "6px 10px", width: "100%", maxWidth: 340,
              }}
              placeholder="Your name"
            />
          ) : (
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#111827" }}>
              {profile.name || "—"}
            </h2>
          )}
          {formik.touched.name && formik.errors.name && isEditing && (
            <p className="error-text" style={{ marginTop: 2 }}>{formik.errors.name}</p>
          )}
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#6b7280" }}>
            {profile.designation || profile.role || "Admin"}
          </p>
        </div>

        {/* ── VIEW MODE ── */}
        {!isEditing && (
          <div style={{ padding: "12px 28px 24px" }}>
            <InfoRow icon={Mail}      label="Email"       value={profile.email} />
            <InfoRow icon={Phone}     label="Phone"       value={profile.phone} />
            <InfoRow icon={MapPin}    label="Country"    value={profile.Country} />
            <InfoRow icon={Globe}     label="Website"     value={profile.website}
              href={profile.website ? (profile.website.startsWith("http") ? profile.website : `https://${profile.website}`) : undefined} />
            <InfoRow icon={Building2} label="Company"     value={profile.companyName} />
            <InfoRow icon={User}      label="Role"        value={profile.role || profile.designation || "Admin"} />
          </div>
        )}

        {/* ── EDIT MODE ── */}
        {isEditing && (
          <div style={{ padding: "20px 28px 28px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 24px" }}>
              <EditField label="Email *"      name="email"       type="email" placeholder="you@example.com"     formik={formik} />
              <EditField label="Phone"        name="phone"                    placeholder="+1 (555) 000-0000"   formik={formik} />
              <EditField label="Country"     name="Country"                 placeholder="City, Country"       formik={formik} />
              <EditField label="Website"      name="website"                  placeholder="https://yoursite.com" formik={formik} />
              <EditField label="Company Name" name="companyName"              placeholder="Your company"        formik={formik} />
            </div>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════
          VISITING CARD
      ══════════════════════════════════ */}
      <div style={{
        marginTop: 24, background: "#fff", borderRadius: 16,
        border: "1px solid #e5e7eb",
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        padding: "24px 28px",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <CreditCard size={18} color="#5c308d" />
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#1f2937" }}>Visiting Card</h3>
          </div>
          {visitingCardSrc && !isEditing && (
            <a href={visitingCardSrc} target="_blank" rel="noopener noreferrer"
              style={{ fontSize: 12, color: "#5c308d", fontWeight: 600, textDecoration: "none" }}>
              View Full Size ↗
            </a>
          )}
        </div>

        {/* VIEW MODE */}
        {!isEditing && (
          visitingCardSrc ? (
            <div style={{ borderRadius: 12, overflow: "hidden", border: "1px solid #e5e7eb", boxShadow: "0 4px 16px rgba(0,0,0,0.08)", maxWidth: 480 }}>
              <FallbackImage src={visitingCardSrc} alt="Visiting Card" style={{ width: "100%", height: "auto", display: "block" }} />
            </div>
          ) : (
            <div style={{ padding: "32px 24px", borderRadius: 12, border: "2px dashed #e5e7eb", background: "#f9fafb", textAlign: "center", color: "#9ca3af" }}>
              <CreditCard size={28} style={{ marginBottom: 8, opacity: 0.4 }} />
              <p style={{ margin: 0, fontSize: 13 }}>No visiting card uploaded yet.</p>
              <p style={{ margin: "4px 0 0", fontSize: 12 }}>Click "Edit Profile" to add one.</p>
            </div>
          )
        )}

        {/* EDIT MODE */}
        {isEditing && (
          <div>
            <div
              onClick={() => cardInputRef.current?.click()}
              style={{
                border: "2px dashed #d1d5db", borderRadius: 12,
                padding: cardPreview ? 12 : "28px 24px",
                background: "#f9fafb", cursor: "pointer",
                textAlign: cardPreview ? "left" : "center",
                transition: "border-color 0.2s",
              }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = "#5c308d"}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = "#d1d5db"}
            >
              {cardPreview ? (
                <div style={{ position: "relative", display: "inline-block" }}>
                  <img src={cardPreview} alt="preview" style={{ maxHeight: 140, borderRadius: 8, display: "block", objectFit: "contain" }} />
                  <button type="button"
                    onClick={(e) => { e.stopPropagation(); setCardPreview(""); formik.setFieldValue("visitingCard", null); }}
                    style={{
                      position: "absolute", top: -8, right: -8,
                      width: 22, height: 22, borderRadius: "50%",
                      background: "#dc2626", color: "#fff", border: "none",
                      cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <>
                  <CreditCard size={24} color="#9ca3af" style={{ marginBottom: 8 }} />
                  <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>Click to upload visiting card</p>
                  <p style={{ margin: "2px 0 0", fontSize: 11, color: "#9ca3af" }}>PNG, JPG up to 5MB</p>
                </>
              )}
            </div>
            <input ref={cardInputRef} type="file" accept=".png,.jpg,.jpeg"
              style={{ display: "none" }} onChange={handleFileChange("visitingCard", setCardPreview)} />
            <p style={{ margin: "8px 0 0", fontSize: 11, color: "#9ca3af" }}>Replaces the current visiting card on save.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
